import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api';
import type { KeySpendOut } from '../types';
import { SpendChart } from './SpendChart';

interface KeySpendViewProps {
  keyId: string;
}

/**
 * Per-key spend chart with loading state. Fetches the key's spend history and renders
 * it as a time-series chart. Placed where per-key detail is shown (e.g., in an expanded
 * row or detail panel).
 */
export function KeySpendView({ keyId }: KeySpendViewProps) {
  const [data, setData] = useState<KeySpendOut | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [days, setDays] = useState(30);
  const generation = useRef(0);

  const load = useCallback(
    async (newDays: number) => {
      const gen = ++generation.current;
      const current = () => gen === generation.current;
      setLoading(true);
      setError(null);
      try {
        const result = await api.keySpend(keyId, newDays);
        if (!current()) return;
        setData(result);
      } catch (err) {
        if (!current()) return;
        setError(err instanceof Error ? err.message : String(err));
        setData(null);
      } finally {
        if (current()) setLoading(false);
      }
    },
    [keyId]
  );

  useEffect(() => {
    void load(days);
    return () => {
      generation.current++;
    };
  }, [load, days]);

  const handleDaysChange = (newDays: number) => {
    setDays(newDays);
  };

  if (loading) {
    return <div className="muted small">Loading spend history...</div>;
  }

  if (error) {
    return <div className="callout callout-warning">Failed to load spend history: {error}</div>;
  }

  if (!data) {
    return <div className="muted small">No spend data available</div>;
  }

  return (
    <div>
      <SpendChart
        points={data.points}
        asOf={data.as_of}
        days={days}
        onDaysChange={handleDaysChange}
      />
    </div>
  );
}
